/**
 * Calibrated Compass Hook for FlowRide Frontend
 * Provides compass functionality with calibration capabilities for navigation.
 * Handles device orientation events and manual calibration for accurate heading.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SensorFusion, type SensorData } from "../libs/SensorFusion";
import { compassPermissionService, type CompassPermissionState } from "../services/navigation/CompassPermissionService";
import {
    CompassHeadingJumpFilter,
    extractAbsoluteCompassHeading,
    normalizeCompassAngle,
    shortestHeadingDeltaDegrees,
    supportsAbsoluteDeviceOrientationEvent,
    type CompassOrientationEvent,
} from "../tools/compass-heading.tools";

export interface CalibratedCompassState {

    /** Current heading in degrees [0,360) */
    "heading": number | null;

    /** Whether the compass is calibrated */
    "calibrated": boolean;

    /** Offset in degrees applied to raw magnetometer reading */
    "offset": number;

    /** Timestamp of last update */
    "lastUpdated": number | null;

    /** iOS permission management */
    "permission": CompassPermissionState;

    /** Function to request device orientation permission */
    "requestPermission": () => Promise<boolean>;

    /** Whether manual calibration is active */
    "manual": boolean;

    /** Function to manually calibrate to a reference bearing */
    "manualCalibrateTo": ( referenceBearing: number ) => void;

    /** Function to clear manual calibration */
    "clearManualCalibration": () => void;

    /** Whether gyroscope is available and enabled */
    "gyroscopeAvailable": boolean;

    /** Function to enable/disable gyroscope */
    "setGyroscopeEnabled": ( enabled: boolean ) => void;

    /** Whether gyroscope is currently enabled */
    "gyroscopeEnabled": boolean;

    /** Function to update GPS position for auto-inversion detection and auto-calibration */
    "updateGpsPosition": ( lat: number, lng: number, timestamp: number, speed?: number, gpsHeading?: number ) => void;

    /** Function to adjust complementary filter coefficient (0-1, higher = more magnetometer) */
    "setFilterCoefficient": ( alpha: number ) => void;

    /** Current complementary filter coefficient */
    "filterCoefficient": number;

    /** Whether AbsoluteOrientationSensor is available */
    "absoluteOrientationAvailable": boolean;

    /** Whether AbsoluteOrientationSensor is currently enabled */
    "absoluteOrientationEnabled": boolean;

    /** Function to enable/disable AbsoluteOrientationSensor */
    "setAbsoluteOrientationEnabled": ( enabled: boolean ) => void;
}

/**
 * Normalize angle to [0, 360) range
 * @param deg - Angle in degrees
 * @returns Normalized angle in [0, 360) range
 */
const norm = ( deg: number ): number => normalizeCompassAngle( deg );

/**
 * Compute shortest signed angle delta between two angles
 * @param from - Starting angle
 * @param to - Target angle
 * @returns Shortest signed angle difference
 */
const angleDelta = ( from: number, to: number ): number => shortestHeadingDeltaDegrees( from, to );

/**
 * Sensor fusion state for compass calculation
 */
interface SensorFusionState {
    "magnetometerHeading":      number | null;
    "gyroscopeHeading":         number | null;
    "lastGyroUpdate":           number;
    "gyroDrift":                number;
    "alpha":                    number; // Complementary filter coefficient (0-1)
    // Enhanced sensor fusion
    "accelerometerGravity":     [number, number, number] | null;
    "gyroscopeBias":            [number, number, number];
    "magnetometerVector":       [number, number, number] | null;
    "lastAccelerometerReading": number;
    "lastMagnetometerReading":  number;
}

/**
 * Check if AbsoluteOrientationSensor is available
 */
function isAbsoluteOrientationSensorAvailable(): boolean {
    return typeof window.AbsoluteOrientationSensor !== "undefined";
}

/**
 * Convert quaternion to compass heading
 * @param quaternion - Quaternion [x, y, z, w]
 * @returns Heading in degrees [0, 360)
 */
function quaternionToHeading( quaternion: readonly [number, number, number, number] ): number {
    const [ x, y, z, w ] = quaternion;

    /*
     * Convert quaternion to Euler angles
     * Roll (x-axis rotation) - not used but kept for reference
     * const sinr_cosp = 2 * (w * x + y * z);
     * const cosr_cosp = 1 - 2 * (x * x + y * y);
     * const roll = Math.atan2(sinr_cosp, cosr_cosp);
     */

    /*
     * Pitch (y-axis rotation) - not used but kept for reference
     * const sinp = 2 * (w * y - z * x);
     * let pitch;
     * if (Math.abs(sinp) >= 1) {
     *     pitch = Math.sign(sinp) * Math.PI / 2; // use 90 degrees if out of range
     * } else {
     *     pitch = Math.asin(sinp);
     * }
     */

    // Yaw (z-axis rotation) - this is our compass heading
    const siny_cosp = 2 * ( w * z + x * y );
    const cosy_cosp = 1 - 2 * ( y * y + z * z );
    const yaw = Math.atan2( siny_cosp, cosy_cosp );

    // Convert to degrees and normalize to [0, 360)
    const heading = ( yaw * 180 / Math.PI + 360 ) % 360;

    return norm( heading );
}

/**
 * Calculate heading from accelerometer and magnetometer data
 * @param accel - Accelerometer data [x, y, z] (including gravity)
 * @param mag - Magnetometer data [x, y, z]
 * @returns Heading in degrees [0, 360)
 * @internal Reserved for future tilt-compensated compass implementation
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function calculateHeadingFromAccelMag( accel: [number, number, number], mag: [number, number, number] ): number {
    // Normalize accelerometer data (gravity vector)
    const accelNorm = Math.sqrt( accel[0] ** 2 + accel[1] ** 2 + accel[2] ** 2 );
    const accelX = accel[0] / accelNorm;
    const accelY = accel[1] / accelNorm;
    const accelZ = accel[2] / accelNorm;

    // Calculate roll and pitch from accelerometer
    const roll = Math.atan2( accelY, accelZ );
    const pitch = Math.atan2( -accelX, Math.sqrt( accelY ** 2 + accelZ ** 2 ) );

    // Rotate magnetometer data to compensate for tilt
    const cosRoll = Math.cos( roll );
    const sinRoll = Math.sin( roll );
    const cosPitch = Math.cos( pitch );
    const sinPitch = Math.sin( pitch );

    const magX = mag[0] * cosPitch + mag[1] * sinRoll * sinPitch + mag[2] * cosRoll * sinPitch;
    const magY = mag[1] * cosRoll - mag[2] * sinRoll;

    // Calculate heading from compensated magnetometer data
    const heading = Math.atan2( -magY, magX ) * 180 / Math.PI;

    return norm( heading );
}

/**
 * Apply complementary filter with gravity compensation
 * @param magnetometerHeading - Current magnetometer reading
 * @param gyroscopeData - Gyroscope data [x, y, z] in degrees/second
 * @param accelerometerData - Accelerometer data [x, y, z] including gravity
 * @param fusionState - Current sensor fusion state
 * @returns Fused heading
 * @internal Reserved for future advanced sensor fusion
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function applyAdvancedComplementaryFilter(
    magnetometerHeading: number,
    gyroscopeData: [number, number, number],
    accelerometerData: [number, number, number],
    fusionState: SensorFusionState,
): number {
    const now = Date.now();
    const deltaTime = ( now - fusionState.lastGyroUpdate ) / 1000; // Convert to seconds

    if ( deltaTime <= 0 ) {
        return magnetometerHeading;
    }

    // Update gyroscope bias estimation (simple bias estimation)
    const biasLearningRate = 0.001;
    fusionState.gyroscopeBias[0] += gyroscopeData[0] * biasLearningRate;
    fusionState.gyroscopeBias[1] += gyroscopeData[1] * biasLearningRate;
    fusionState.gyroscopeBias[2] += gyroscopeData[2] * biasLearningRate;

    /*
     * Apply bias correction to gyroscope data
     * const correctedGyroX = gyroscopeData[0] - fusionState.gyroscopeBias[0]; // Not used
     * const correctedGyroY = gyroscopeData[1] - fusionState.gyroscopeBias[1]; // Not used
     */
    const correctedGyroZ = gyroscopeData[2] - fusionState.gyroscopeBias[2];

    /*
     * Calculate heading from accelerometer and magnetometer (tilt-compensated) - not used but useful for reference
     * const accelMagHeading = calculateHeadingFromAccelMag(accelerometerData, fusionState.magnetometerVector || [0, 0, 0]);
     */

    // Update gyroscope heading with corrected data
    if ( fusionState.gyroscopeHeading !== null ) {
    // Use Z-axis gyroscope data for heading (rotation around vertical axis)
        fusionState.gyroscopeHeading = norm( fusionState.gyroscopeHeading + correctedGyroZ * deltaTime );
    } else {
        fusionState.gyroscopeHeading = magnetometerHeading;
    }

    // Apply complementary filter
    const alpha = fusionState.alpha;
    const fusedHeading = norm( alpha * magnetometerHeading +
      ( 1 - alpha ) * fusionState.gyroscopeHeading );

    // Update state
    fusionState.gyroscopeHeading = fusedHeading;
    fusionState.lastGyroUpdate = now;
    fusionState.accelerometerGravity = accelerometerData;
    fusionState.lastAccelerometerReading = now;

    return fusedHeading;
}

/**
 * Extract compass heading from DeviceOrientationEvent.
 * Ignores relative Game Rotation Vector samples unless absolute data has never arrived.
 * @param evt - Device orientation event
 * @param allowRelativeFallback - Whether non-absolute alpha may be used
 * @returns Compass heading in degrees or null if not available
 */
function extractCompassHeading(
    evt: CompassOrientationEvent,
    allowRelativeFallback: boolean = false,
): number | null {
    return extractAbsoluteCompassHeading( evt, { allowRelativeFallback } );
}

/**
 * Apply complementary filter to fuse magnetometer and gyroscope data
 * @param magnetometerHeading - Current magnetometer reading
 * @param fusionState - Current sensor fusion state
 * @returns Fused heading
 */
function applyComplementaryFilter( magnetometerHeading: number, fusionState: SensorFusionState ): number {
    const now = Date.now();
    const deltaTime = ( now - fusionState.lastGyroUpdate ) / 1000; // Convert to seconds

    // Update gyroscope heading with drift correction
    if ( fusionState.gyroscopeHeading !== null && deltaTime > 0 ) {
    // Apply gyroscope drift correction
        fusionState.gyroscopeHeading = norm( fusionState.gyroscopeHeading + fusionState.gyroDrift * deltaTime );
    }

    // Complementary filter: combine magnetometer (low-pass) and gyroscope (high-pass)
    if ( fusionState.gyroscopeHeading !== null ) {
        const fusedHeading = norm( fusionState.alpha * magnetometerHeading +
          ( 1 - fusionState.alpha ) * fusionState.gyroscopeHeading );

        // Update gyroscope heading for next iteration
        fusionState.gyroscopeHeading = fusedHeading;
        fusionState.lastGyroUpdate = now;

        return fusedHeading;
    } else {
    // Initialize gyroscope heading with magnetometer reading
        fusionState.gyroscopeHeading = magnetometerHeading;
        fusionState.lastGyroUpdate = now;

        return magnetometerHeading;
    }
}

/**
 * Calculate bearing between two GPS coordinates
 * @param lat1 - Latitude of first point
 * @param lng1 - Longitude of first point
 * @param lat2 - Latitude of second point
 * @param lng2 - Longitude of second point
 * @returns Bearing in degrees [0,360)
 */
function calculateBearing( lat1: number, lng1: number, lat2: number, lng2: number ): number {
    const toRad = ( deg: number ) => ( deg * Math.PI ) / 180;
    const toDeg = ( rad: number ) => ( rad * 180 ) / Math.PI;

    const lat1Rad = toRad( lat1 );
    const lat2Rad = toRad( lat2 );
    const deltaLngRad = toRad( lng2 - lng1 );

    const y = Math.sin( deltaLngRad ) * Math.cos( lat2Rad );
    const x = Math.cos( lat1Rad ) * Math.sin( lat2Rad ) - Math.sin( lat1Rad ) * Math.cos( lat2Rad ) * Math.cos( deltaLngRad );

    const bearing = toDeg( Math.atan2( y, x ) );

    return norm( bearing );
}

/**
 * Calculate distance between two GPS coordinates using Haversine formula
 * @param lat1 - Latitude of first point
 * @param lng1 - Longitude of first point
 * @param lat2 - Latitude of second point
 * @param lng2 - Longitude of second point
 * @returns Distance in meters
 */
function calculateDistance( lat1: number, lng1: number, lat2: number, lng2: number ): number {
    const R = 6371000; // Earth's radius in meters
    const toRad = ( deg: number ) => ( deg * Math.PI ) / 180;

    const lat1Rad = toRad( lat1 );
    const lat2Rad = toRad( lat2 );
    const deltaLatRad = toRad( lat2 - lat1 );
    const deltaLngRad = toRad( lng2 - lng1 );

    const a = Math.sin( deltaLatRad / 2 ) * Math.sin( deltaLatRad / 2 ) +
      Math.cos( lat1Rad ) * Math.cos( lat2Rad ) *
      Math.sin( deltaLngRad / 2 ) * Math.sin( deltaLngRad / 2 );
    const c = 2 * Math.atan2( Math.sqrt( a ), Math.sqrt( 1 - a ) );

    return R * c;
}

/**
 * Hook: useCalibratedCompass
 * Reads device orientation (magnetometer/IMU) events and provides calibrated compass functionality.
 * Supports manual calibration and automatic offset calculation for accurate navigation.
 *
 * Features:
 * - Reads device orientation events
 * - Manual calibration to reference bearings
 * - iOS permission management
 * - Screen orientation compensation
 */
export const useCalibratedCompass = (): CalibratedCompassState => {
    const [ state, setState ] = useState<Omit<CalibratedCompassState, "permission" | "requestPermission" | "manual" | "manualCalibrateTo" | "clearManualCalibration" | "gyroscopeAvailable" | "setGyroscopeEnabled" | "gyroscopeEnabled" | "updateGpsPosition" | "setFilterCoefficient" | "filterCoefficient" | "absoluteOrientationAvailable" | "absoluteOrientationEnabled" | "setAbsoluteOrientationEnabled">>( {
        "heading":     null,
        "calibrated":  false,
        "offset":      0,
        "lastUpdated": null,
    } );
    const [ permission, setPermission ] = useState<CompassPermissionState>( "unknown" );
    const [ manual, setManual ] = useState( false );
    const [ gyroscopeEnabled, setGyroscopeEnabled ] = useState( false );
    const [ absoluteOrientationEnabled, setAbsoluteOrientationEnabled ] = useState( false );
    const rawMagHeadingRef = useRef<number | null>( null );
    const lastEventTs = useRef<number>( 0 );
    const manualLockRef = useRef( false );

    // GPS tracking for auto-inversion detection
    const gpsPositionsRef = useRef<Array<{
        "lat":            number;
        "lng":            number;
        "timestamp":      number;
        "compassHeading": number | null;
    }>>( [] );

    // GPS auto-calibration tracking
    const lastGpsCalibrationRef = useRef<number>( 0 );
    const GPS_CALIBRATION_INTERVAL = 60000; // 1 minute in milliseconds
    // Unified calibration: disable legacy GPS-based auto-calibration in favor of fused 10s routine
    const ENABLE_GPS_CALIBRATION = false;

    // GPS positions for initial calibration (5 positions at >5 km/h)
    const gpsCalibrationPositionsRef = useRef<Array<{
        "lat":        number;
        "lng":        number;
        "timestamp":  number;
        "speed":      number;
        "gpsHeading": number;
    }>>( [] );
    const INITIAL_CALIBRATION_POSITIONS = 5;
    const MIN_SPEED_FOR_CALIBRATION = 1.39; // 5 km/h in m/s

    // Sensor fusion state
    const fusionStateRef = useRef<SensorFusionState>( {
        "magnetometerHeading":      null,
        "gyroscopeHeading":         null,
        "lastGyroUpdate":           Date.now(),
        "gyroDrift":                0,
        "alpha":                    0.98, // Complementary filter coefficient (higher = more magnetometer, lower = more gyroscope)
        // Enhanced sensor fusion
        "accelerometerGravity":     null,
        "gyroscopeBias":            [ 0, 0, 0 ],
        "magnetometerVector":       null,
        "lastAccelerometerReading": 0,
        "lastMagnetometerReading":  0,
    } );

    // AbsoluteOrientationSensor reference
    const absoluteOrientationSensorRef = useRef<AbsoluteOrientationSensor | null>( null );

    // Advanced sensor fusion instance
    const sensorFusionRef = useRef<SensorFusion | null>( null );

    /** Rejects transient ~90° discontinuities on relative/absolute Euler streams. */
    const headingJumpFilterRef = useRef( new CompassHeadingJumpFilter() );

    /** True once a north-referenced orientation sample was observed. */
    const absoluteOrientationSeenRef = useRef( false );

    // Automatic calibration and snapping removed: heading strictly uses manual offset

    // Initialize AbsoluteOrientationSensor if available
    useEffect( () => {
        if ( !isAbsoluteOrientationSensorAvailable() || !absoluteOrientationEnabled ) {
            return;
        }

        try {
            if ( !window.AbsoluteOrientationSensor ) {
                return;
            }
            const sensor = new window.AbsoluteOrientationSensor( { "frequency": 60 } );
            absoluteOrientationSensorRef.current = sensor;
            headingJumpFilterRef.current.reset();

            const onReading = () => {
                if ( sensor.quaternion === null || sensor.quaternion === undefined ) {
                    return;
                }

                const heading = quaternionToHeading( sensor.quaternion );
                const filteredHeading = headingJumpFilterRef.current.filter( heading );

                absoluteOrientationSeenRef.current = true;
                rawMagHeadingRef.current = filteredHeading;
                const effectiveOffset = state.offset;
                const finalHeading = norm( filteredHeading + effectiveOffset );
                lastEventTs.current = Date.now();

                setState( ( s ) => ( {
                    ...s,
                    "heading":     finalHeading,
                    "lastUpdated": lastEventTs.current,
                } ) );
            };

            sensor.addEventListener( "reading", onReading );
            sensor.addEventListener( "error", ( event: Event ) => {
                const errorEvent = event as Event & { "error"?: Error };
                console.error( "[Compass] AbsoluteOrientationSensor error:", errorEvent.error );
            } );

            sensor.start();

            return () => {
                sensor.stop();
                sensor.removeEventListener( "reading", onReading );
                absoluteOrientationSensorRef.current = null;
            };
        } catch( error: unknown ) {
            console.error( "[Compass] Failed to initialize AbsoluteOrientationSensor:", error );
        }
    }, [ absoluteOrientationEnabled, state.offset ] );

    // Initialize sensor fusion
    useEffect( () => {
        if ( gyroscopeEnabled && !sensorFusionRef.current ) {
            sensorFusionRef.current = new SensorFusion( state.heading || 0, fusionStateRef.current.alpha );

            // ( "[Compass] Initialized advanced sensor fusion" );
        }
    }, [ gyroscopeEnabled, state.heading ] );

    // Listener setup, controlled by permission state (fallback for older APIs)
    useEffect( () => {
    // Skip if AbsoluteOrientationSensor is active (it takes priority)
        if ( absoluteOrientationEnabled && absoluteOrientationSensorRef.current ) {
            return;
        }

        const cleanup: ( () => void )[] = [];
        absoluteOrientationSeenRef.current = false;
        headingJumpFilterRef.current.reset();

        const onOrientation = ( evt: CompassOrientationEvent ) => {
            const eventType = ( evt as Event ).type;
            const isAbsoluteSample =
                eventType === "deviceorientationabsolute" ||
                evt.absolute === true ||
                typeof evt.webkitCompassHeading === "number";

            if ( isAbsoluteSample ) {
                absoluteOrientationSeenRef.current = true;
            }

            const mag = extractCompassHeading(
                evt,
                !supportsAbsoluteDeviceOrientationEvent() && !absoluteOrientationSeenRef.current,
            );

            if ( mag === null ) {
                return;
            }

            const filteredHeading = headingJumpFilterRef.current.filter( mag );
            rawMagHeadingRef.current = filteredHeading;

            const effectiveOffset = state.offset;
            const heading = norm( filteredHeading + effectiveOffset );
            lastEventTs.current = Date.now();

            setState( ( s ) => ( {
                ...s,
                heading,
                "lastUpdated": lastEventTs.current,
            } ) );
        };

        const onMotion = ( evt: DeviceMotionEvent ) => {
            if ( !gyroscopeEnabled ) {
                return;
            }

            // If sensor fusion is not available, just log the data
            if ( !sensorFusionRef.current ) {
                return;
            }

            // Extract sensor data for advanced fusion
            const gyroscopeData: [number, number, number] = [
                evt.rotationRate?.alpha ?? 0,
                evt.rotationRate?.beta ?? 0,
                evt.rotationRate?.gamma ?? 0,
            ];

            const accelerometerData: [number, number, number] = [
                evt.accelerationIncludingGravity?.x ?? 0,
                evt.accelerationIncludingGravity?.y ?? 0,
                evt.accelerationIncludingGravity?.z ?? 0,
            ];

            /*
             * Create magnetometer data from orientation event (simplified)
             * Default values if orientation event not available
             */
            const magnetometerData: [number, number, number] = [ 1, 0, 0 ];

            // Update sensor fusion with new data
            const sensorData: SensorData = {
                "accelerometer": accelerometerData,
                "gyroscope":     gyroscopeData,
                "magnetometer":  magnetometerData,
                "timestamp":     evt.timeStamp,
            };

            const fusionResult = sensorFusionRef.current.update( sensorData );
            const filteredHeading = headingJumpFilterRef.current.filter( fusionResult.heading );

            // Apply manual offset and update state
            const effectiveOffset = state.offset;
            const heading = norm( filteredHeading + effectiveOffset );
            lastEventTs.current = Date.now();

            rawMagHeadingRef.current = filteredHeading;
            setState( ( s ) => ( {
                ...s,
                heading,
                "lastUpdated": lastEventTs.current,
            } ) );
        };

        const addListeners = () => {
            if ( supportsAbsoluteDeviceOrientationEvent() ) {
                window.addEventListener( "deviceorientationabsolute", onOrientation as EventListener );
                cleanup.push( () => {
                    window.removeEventListener( "deviceorientationabsolute", onOrientation as EventListener );
                } );
            }

            window.addEventListener( "deviceorientation", onOrientation as EventListener );
            cleanup.push( () => {
                window.removeEventListener( "deviceorientation", onOrientation as EventListener );
            } );

            if ( gyroscopeEnabled ) {
                window.addEventListener( "devicemotion", onMotion );
                cleanup.push( () => {
                    window.removeEventListener( "devicemotion", onMotion );
                } );
            }
        };

        if ( permission === "granted" || permission === "not_required" ) {
            addListeners();
        }

        return () => {
            cleanup.forEach( ( cleanupFn ) => cleanupFn() );
        };
    }, [ permission, state.offset, state.calibrated, gyroscopeEnabled, absoluteOrientationEnabled ] );

    // Manual calibration APIs
    const manualCalibrateTo = useCallback( ( referenceBearing: number ) => {
        const mag = rawMagHeadingRef.current;
        if ( mag == null || Number.isNaN( referenceBearing ) ) {
            /*
             *  console.log( "[Compass] Cannot calibrate: rawMagHeading is null or referenceBearing is NaN", {
             *     "rawMagHeading": mag,
             *     referenceBearing,
             * } );
             */

            return;
        }
        const off = angleDelta( mag, norm( referenceBearing ) );
        manualLockRef.current = true;
        setManual( true );
        setState( ( s ) => ( {
            ...s,
            "calibrated": true,
            "offset":     off,
        } ) );

        /*
         *  console.log( "[Compass] Manual calibration applied:", {
         *     "rawMagHeading":    mag,
         *     referenceBearing,
         *     "calculatedOffset": off,
         *     "finalHeading":     norm( mag + off ),
         * } );
         */
    }, [] );

    const clearManualCalibration = useCallback( () => {
        manualLockRef.current = false;
        setManual( false );
    }, [] );

    // Function to adjust complementary filter coefficient
    const setFilterCoefficient = useCallback( ( alpha: number ) => {
    // Clamp alpha between 0 and 1
        const clampedAlpha = Math.max( 0, Math.min( 1, alpha ) );
        fusionStateRef.current.alpha = clampedAlpha;
    }, [] );

    // Check gyroscope availability
    const checkGyroscopeAvailability = useCallback( async(): Promise<boolean> => {
        return new Promise( ( resolve ) => {
            if ( !( "DeviceMotionEvent" in window ) ) {
                // console.log( "[Compass] DeviceMotionEvent not supported" );
                resolve( false );

                return;
            }

            let hasGyro = false;
            let timeoutId: NodeJS.Timeout;

            const testMotion = ( event: DeviceMotionEvent ) => {
                /*
                 *  console.log( "[Compass] DeviceMotionEvent received:", {
                 *     "rotationRate":                 event.rotationRate,
                 *     "acceleration":                 event.acceleration,
                 *     "accelerationIncludingGravity": event.accelerationIncludingGravity,
                 * } );
                 */

                if ( event.rotationRate &&
                  ( typeof event.rotationRate.alpha === "number" ||
                    typeof event.rotationRate.beta === "number" ||
                    typeof event.rotationRate.gamma === "number" ) ) {
                    hasGyro = true;

                    // console.log( "[Compass] Gyroscope data detected" );
                }

                window.removeEventListener( "devicemotion", testMotion );
                clearTimeout( timeoutId );
                resolve( hasGyro );
            };

            // Request permission for DeviceMotionEvent (skip when already granted via the service)
            const DeviceMotionEventTyped = DeviceMotionEvent as unknown as { "requestPermission"?: () => Promise<"granted" | "denied"> };
            if ( typeof DeviceMotionEventTyped.requestPermission === "function" && !compassPermissionService.isPermissionGranted() ) {
                // console.log( "[Compass] Requesting DeviceMotionEvent permission" );
                DeviceMotionEventTyped.requestPermission().then( ( response: "granted" | "denied" ) => {
                    console.log( "[Compass] DeviceMotionEvent permission response:", response );
                    if ( response === "granted" ) {
                        window.addEventListener( "devicemotion", testMotion );

                        // Timeout after 2 seconds
                        timeoutId = setTimeout( () => {
                            window.removeEventListener( "devicemotion", testMotion );

                            // console.log( "[Compass] Gyroscope detection timeout" );
                            resolve( hasGyro );
                        }, 2000 );
                    } else {
                        // console.log( "[Compass] DeviceMotionEvent permission denied" );
                        resolve( false );
                    }
                } ).
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    catch( ( error: unknown ) => {
                        // console.error( "[Compass] DeviceMotionEvent permission request failed:", error );
                        resolve( false );
                    } );
            } else {
                /*
                 * For browsers that don't require permission
                 * console.log( "[Compass] DeviceMotionEvent permission not required, testing directly" );
                 */
                window.addEventListener( "devicemotion", testMotion );
                timeoutId = setTimeout( () => {
                    window.removeEventListener( "devicemotion", testMotion );

                    // console.log( "[Compass] Gyroscope detection timeout (no permission required)" );
                    resolve( hasGyro );
                }, 2000 );
            }
        } );
    }, [] );

    // Function to calculate average GPS bearing from positions
    const calculateAverageGpsBearing = useCallback( ( positions: Array<{
        "lat":        number;
        "lng":        number;
        "timestamp":  number;
        "speed":      number;
        "gpsHeading": number;
    }> ): number | null => {
        if ( positions.length < 2 ) {
            return null;
        }

        // Calculate bearings between consecutive positions
        const bearings: number[] = [];
        const individualBearings: Array<{
            "from":    number;
            "to":      number;
            "bearing": number;
        }> = [];

        for ( let i = 1; i < positions.length; i++ ) {
            const prev = positions[i - 1];
            const curr = positions[i];
            const bearing = calculateBearing( prev.lat, prev.lng, curr.lat, curr.lng );
            bearings.push( bearing );
            individualBearings.push( {
                "from":    i - 1,
                "to":      i,
                "bearing": bearing,
            } );
        }

        if ( bearings.length === 0 ) {
            return null;
        }

        // Calculate average bearing (handle 0°/360° wraparound)
        let sumSin = 0;
        let sumCos = 0;

        for ( const bearing of bearings ) {
            const rad = bearing * Math.PI / 180;
            sumSin += Math.sin( rad );
            sumCos += Math.cos( rad );
        }

        const avgRad = Math.atan2( sumSin, sumCos );
        const avgBearing = ( avgRad * 180 / Math.PI + 360 ) % 360;

        // Also calculate average of GPS headings from the positions
        const gpsHeadings = positions.map( ( p ) => p.gpsHeading ).filter( ( h ): h is number => typeof h === "number" );

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const avgGpsHeading = gpsHeadings.length > 0 ? gpsHeadings.reduce( ( sum, h ) => sum + h, 0 ) / gpsHeadings.length : 0;

        /*
         *  console.log( "[Compass] GPS bearing calculation details:", {
         *     "positions":          positions.length,
         *     "individualBearings": individualBearings.map( ( b ) => ( {
         *         "from":    b.from,
         *         "to":      b.to,
         *         "bearing": b.bearing.toFixed( 1 ),
         *     } ) ),
         *     "calculatedBearings":   bearings.map( ( b ) => b.toFixed( 1 ) ),
         *     "avgCalculatedBearing": avgBearing.toFixed( 1 ),
         *     "avgGpsHeading":        avgGpsHeading.toFixed( 1 ),
         *     "difference":           Math.abs( avgBearing - avgGpsHeading ).toFixed( 1 ),
         * } );
         */

        return avgBearing;
    }, [] );

    // Function to detect compass inversion based on GPS movement
    const checkForInversion = useCallback( () => {
        const positions = gpsPositionsRef.current;
        if ( positions.length < 5 ) {
            return;
        }

        // Calculate total distance moved
        let totalDistance = 0;
        for ( let i = 1; i < positions.length; i++ ) {
            const prev = positions[i - 1];
            const curr = positions[i];
            totalDistance += calculateDistance( prev.lat, prev.lng, curr.lat, curr.lng );
        }

        // Only check for inversion if we've moved at least 50 meters
        if ( totalDistance < 50 ) {
            // console.log( `[Compass] Not enough movement for inversion detection: ${ totalDistance.toFixed( 1 ) }m < 50m` );

            return;
        }

        // Calculate movement vectors and compass directions
        const movements: Array<{
            "gpsBearing":     number;
            "compassHeading": number | null;
            "distance":       number;
        }> = [];

        for ( let i = 1; i < positions.length; i++ ) {
            const prev = positions[i - 1];
            const curr = positions[i];

            // Calculate distance for this segment
            const segmentDistance = calculateDistance( prev.lat, prev.lng, curr.lat, curr.lng );

            // Only consider segments with significant movement (>5m)
            if ( segmentDistance < 5 ) {
                continue;
            }

            // Calculate GPS bearing (actual direction of movement)
            const gpsBearing = calculateBearing( prev.lat, prev.lng, curr.lat, curr.lng );

            // Get compass heading at this position
            const compassHeading = curr.compassHeading;

            if ( compassHeading !== null ) {
                movements.push( {
                    gpsBearing,
                    compassHeading,
                    "distance": segmentDistance,
                } );
            }
        }

        if ( movements.length < 3 ) {
            return;
        }

        // Check if compass is consistently 180° off from GPS bearing
        let totalMovements = 0;
        let weightedInversionScore = 0;
        let totalWeight = 0;

        for ( const movement of movements ) {
            const angleDiff = Math.abs( angleDelta( movement.compassHeading ?? 0, movement.gpsBearing ) );
            totalMovements++;

            // Weight by distance moved (longer movements are more reliable)
            const weight = movement.distance;
            totalWeight += weight;

            // If difference is close to 180°, it's likely an inversion
            if ( angleDiff > 150 && angleDiff < 210 ) {
                weightedInversionScore += weight;
            }
        }

        // Calculate weighted inversion percentage
        const weightedInversionPercentage = totalWeight > 0 ? weightedInversionScore / totalWeight : 0;

        // If more than 70% of weighted movements show inversion, auto-correct
        if ( totalMovements >= 3 && weightedInversionPercentage > 0.7 ) {
            // console.log( `[Compass] Auto-detected inversion after ${ totalDistance.toFixed( 1 ) }m movement, applying 180° correction` );
            setState( ( s ) => ( {
                ...s,
                "offset": s.offset + 180,
            } ) );

            // Clear GPS positions to avoid repeated corrections
            gpsPositionsRef.current = [];
        } else {
            // console.log( `[Compass] No inversion detected: ${ ( weightedInversionPercentage * 100 ).toFixed( 1 ) }% inversion score, ${ totalDistance.toFixed( 1 ) }m total movement` );
        }
    }, [] );

    // Function to update GPS position for auto-inversion detection and auto-calibration
    const updateGpsPosition = useCallback( ( lat: number, lng: number, timestamp: number, speed?: number, gpsHeading?: number ) => {
        const compassHeading = state.heading;

        // Add new position
        gpsPositionsRef.current.push( {
            lat,
            lng,
            timestamp,
            compassHeading,
        } );

        // Keep only last 10 positions
        if ( gpsPositionsRef.current.length > 10 ) {
            gpsPositionsRef.current = gpsPositionsRef.current.slice( -10 );
        }

        // Legacy GPS auto-calibration disabled (unified fused calibration elsewhere)
        if ( ENABLE_GPS_CALIBRATION && speed && speed > MIN_SPEED_FOR_CALIBRATION && gpsHeading !== null && !Number.isNaN( gpsHeading ) ) {
            const now = Date.now();

            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const speedKmh = speed * 3.6;

            // Add position to calibration tracking
            gpsCalibrationPositionsRef.current.push( {
                lat,
                lng,
                "timestamp":  now,
                speed,
                "gpsHeading": gpsHeading!,
            } );

            // Keep only recent positions (last 30 seconds)
            const cutoff = now - 30000;
            gpsCalibrationPositionsRef.current = gpsCalibrationPositionsRef.current.filter( ( p ) => p.timestamp > cutoff );

            // Check if we have enough positions for initial calibration
            const hasEnoughPositions = gpsCalibrationPositionsRef.current.length >= INITIAL_CALIBRATION_POSITIONS;
            const timeSinceLastCalibration = now - lastGpsCalibrationRef.current;
            const canCalibratePeriodically = timeSinceLastCalibration >= GPS_CALIBRATION_INTERVAL;

            if ( hasEnoughPositions && lastGpsCalibrationRef.current === 0 ) {
                // Initial calibration with 5 positions
                const avgGpsBearing = calculateAverageGpsBearing( gpsCalibrationPositionsRef.current );

                // Also calculate average GPS heading directly
                const gpsHeadings = gpsCalibrationPositionsRef.current.map( ( p ) => p.gpsHeading );
                const avgGpsHeading = gpsHeadings.reduce( ( sum, h ) => sum + h, 0 ) / gpsHeadings.length;

                if ( avgGpsBearing !== null ) {
                    /*
                     *  console.log( "[Compass] GPS initial calibration triggered:", {
                     *      "speed":             speedKmh.toFixed( 1 ) + " km/h",
                     *      "positions":         gpsCalibrationPositionsRef.current.length,
                     *      "avgGpsBearing":     avgGpsBearing.toFixed( 1 ),
                     *      "avgGpsHeading":     avgGpsHeading.toFixed( 1 ),
                     *      "compassHeading":    compassHeading?.toFixed( 1 ),
                     *      "speedThreshold":    "5 km/h",
                     *      "calibrationType":   "initial (5 positions)",
                     *      "bearingDifference": Math.abs( avgGpsBearing - avgGpsHeading ).toFixed( 1 ),
                     * } );
                     */

                    /*
                     * Use GPS heading average instead of calculated bearing for now
                     * This might be more accurate than the calculated bearing
                     */
                    const calibrationValue = avgGpsHeading;

                    /*
                     * console.log( "[Compass] Using GPS heading average for calibration:", {
                     *     "calculatedBearing": avgGpsBearing?.toFixed( 1 ) ?? "null",
                     *     "gpsHeadingAverage": avgGpsHeading.toFixed( 1 ),
                     *     "chosenValue":       calibrationValue.toFixed( 1 ),
                     * } );
                     */

                    // Apply GPS calibration
                    manualCalibrateTo( calibrationValue );

                    // Update last calibration time
                    lastGpsCalibrationRef.current = now;

                    // Clear calibration positions
                    gpsCalibrationPositionsRef.current = [];
                }
            } else if ( canCalibratePeriodically && lastGpsCalibrationRef.current > 0 ) {
                /*
                 *  Periodic calibration (once per minute after initial)
                 *  console.log( "[Compass] GPS periodic calibration triggered:", {
                 *     "speed":                    speedKmh.toFixed( 1 ) + " km/h",
                 *     "gpsHeading":               gpsHeading?.toFixed( 1 ) ?? "undefined",
                 *     "compassHeading":           compassHeading?.toFixed( 1 ) ?? "undefined",
                 *     "timeSinceLastCalibration": Math.round( timeSinceLastCalibration / 1000 ) + "s",
                 *     "calibrationType":          "periodic (1 minute)",
                 * } );
                 */

                // Apply GPS calibration
                if ( gpsHeading != null ) {
                    manualCalibrateTo( gpsHeading );
                }

                // Update last calibration time
                lastGpsCalibrationRef.current = now;

                // Clear calibration positions
                gpsCalibrationPositionsRef.current = [];
            } else {
                // Log that calibration is skipped
                if ( lastGpsCalibrationRef.current === 0 ) {
                    /*
                     *  console.log( "[Compass] GPS calibration pending (collecting positions):", {
                     *     "positions":  gpsCalibrationPositionsRef.current.length,
                     *     "required":   INITIAL_CALIBRATION_POSITIONS,
                     *     "speed":      speedKmh.toFixed( 1 ) + " km/h",
                     *     "gpsHeading": gpsHeading?.toFixed( 1 ) ?? "undefined",
                     * } );
                     */
                } else {
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const remainingTime = Math.round( ( GPS_CALIBRATION_INTERVAL - timeSinceLastCalibration ) / 1000 );

                    /*
                     *  console.log( "[Compass] GPS calibration skipped (frequency limit):", {
                     *     "remainingTime": remainingTime + "s",
                     *     "speed":         speedKmh.toFixed( 1 ) + " km/h",
                     *     "gpsHeading":    gpsHeading?.toFixed( 1 ) ?? "undefined",
                     * } );
                     */
                }
            }
        }

        // Check for inversion if we have at least 5 positions (always, regardless of calibration)
        if ( gpsPositionsRef.current.length >= 5 ) {
            checkForInversion();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ state.heading, checkForInversion, manualCalibrateTo, calculateAverageGpsBearing ] );

    // Check gyroscope availability
    const [ gyroscopeAvailable, setGyroscopeAvailable ] = useState( false );

    // Check AbsoluteOrientationSensor availability
    const [ absoluteOrientationAvailable, setAbsoluteOrientationAvailable ] = useState( false );

    useEffect( () => {
        const checkSensors = async() => {
            // Check AbsoluteOrientationSensor first (highest priority)
            const absOrientationAvailable = isAbsoluteOrientationSensorAvailable();
            setAbsoluteOrientationAvailable( absOrientationAvailable );

            if ( absOrientationAvailable && ( permission === "granted" || permission === "not_required" ) ) {
                // console.log( "[Compass] Auto-enabling AbsoluteOrientationSensor (highest priority)" );
                setAbsoluteOrientationEnabled( true );

                return; // Skip gyroscope check if AbsoluteOrientationSensor is available
            }

            // Depends on GPS calibration setting and calculation function

            // Fallback to gyroscope if AbsoluteOrientationSensor is not available
            const gyroAvailable = await checkGyroscopeAvailability();
            setGyroscopeAvailable( gyroAvailable );

            if ( gyroAvailable && ( permission === "granted" || permission === "not_required" ) ) {
                // console.log( "[Compass] Auto-enabling gyroscope (fallback)" );
                setGyroscopeEnabled( true );
            }
        };

        void checkSensors();
    }, [ checkGyroscopeAvailability, permission ] );

    // Subscribe to compass permission state changes from CompassPermissionService
    useEffect( () => {
        compassPermissionService.subscribeToPermissionState( setPermission );
    }, [] );

    const requestPermission = useCallback( async(): Promise<boolean> => {
        return await compassPermissionService.requestPermission();
    }, [] );

    return useMemo(
        () => ( {
            ...state,
            permission,
            requestPermission,
            manual,
            manualCalibrateTo,
            clearManualCalibration,
            gyroscopeAvailable,
            setGyroscopeEnabled,
            gyroscopeEnabled,
            updateGpsPosition,
            setFilterCoefficient,
            "filterCoefficient": fusionStateRef.current.alpha,
            absoluteOrientationAvailable,
            absoluteOrientationEnabled,
            setAbsoluteOrientationEnabled,
        } ),
        [ state, permission, requestPermission, manual, manualCalibrateTo, clearManualCalibration, gyroscopeAvailable, gyroscopeEnabled, updateGpsPosition, setFilterCoefficient, absoluteOrientationAvailable, absoluteOrientationEnabled ],
    );
};
